import { EventDropArg } from '@fullcalendar/core';
import { Box, Button, CircularProgress, Paper, Typography } from '@mui/material';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAppSelector } from '../../store/hooks';
import { DashboardMonthlyRequest, DashboardOrderSummary, OrderStatus, UserRole } from '../../types';
import { showToast } from '../../utils/toast-service';
import { useGetActiveManufacturersQuery } from '../manufacturer/manufacturers-api-slice';
import NewOrderWizard from '../order/new-order-wizard';
import {
  useGetMonthlyDashboardQuery,
  useUpdateOrderDueDateMutation,
} from '../order/orders-api-slice';
import { selectCurrentUser, selectManageOrders } from '../user';
import { DashboardCalendar } from './dashboard-calendar';
import { DashboardFilters } from './dashboard-filters';
import { DashboardStatistics } from './dashboard-statistics';
import { OrderDetailPopup } from './order-detail-popup';

const DashboardPage = () => {
  const currentUser = useAppSelector(selectCurrentUser);
  const canManageOrders = useAppSelector(selectManageOrders);
  const isAdmin = currentUser?.role === UserRole.ADMINISTRATOR;
  const isSupervisor = currentUser?.role === UserRole.SUPERVISOR;
  const canReschedule = isAdmin || isSupervisor;
  const [showNewOrderWizard, setShowNewOrderWizard] = useState(false);

  // Fetch manufacturers for admin users
  const { data: manufacturers = [] } = useGetActiveManufacturersQuery(undefined, {
    skip: !isAdmin,
  });

  // State for current view
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState<'month' | 'week'>('month');
  // Track visible date range for week view (from FullCalendar)
  const [visibleDateRange, setVisibleDateRange] = useState<{ start: Date; end: Date } | null>(null);

  // Filter state - for non-admins, use their manufacturerId; for admins, default to first manufacturer
  const [selectedManufacturerId, setSelectedManufacturerId] = useState<number | undefined>(
    currentUser?.manufacturerId || undefined
  );

  // For admins, default to first manufacturer if none selected
  useEffect(() => {
    if (isAdmin && manufacturers.length > 0 && !selectedManufacturerId) {
      setSelectedManufacturerId(manufacturers[0].id);
    }
  }, [isAdmin, manufacturers, selectedManufacturerId]);
  const [pastDueOnly, setPastDueOnly] = useState(false);
  const [dueWithin7Days, setDueWithin7Days] = useState(false);
  const [showPONumbers, setShowPONumbers] = useState(false);
  const [selectedStatuses, setSelectedStatuses] = useState<OrderStatus[]>([]); // No filters selected by default

  // Popup state
  const [selectedOrder, setSelectedOrder] = useState<DashboardOrderSummary | null>(null);
  const [popupOpen, setPopupOpen] = useState(false);

  // Calculate year and month
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth() + 1;

  // Build request - skip if manufacturerId is not set
  const dashboardRequest: DashboardMonthlyRequest | null = useMemo(() => {
    if (!selectedManufacturerId) {
      return null;
    }

    // For week view, use date range if available (preferred method)
    let startDate: string | undefined;
    let endDate: string | undefined;

    if (viewMode === 'week' && visibleDateRange) {
      // Format dates as ISO strings (YYYY-MM-DD)
      startDate = DateTime.fromJSDate(visibleDateRange.start).toISODate() || undefined;
      endDate = DateTime.fromJSDate(visibleDateRange.end).toISODate() || undefined;
    }

    return {
      year,
      month,
      manufacturerId: selectedManufacturerId,
      statusFilters: selectedStatuses.length > 0 ? selectedStatuses : undefined,
      pastDueOnly: pastDueOnly || undefined,
      dueWithin7Days: dueWithin7Days || undefined,
      showWeekly: viewMode === 'week',
      startDate,
      endDate,
      showPONumbers,
      includeKpi: true, // Always include KPI metrics
      kpiViewMode: viewMode, // Use the same view mode as the calendar
      includePartialPeriods: true,
    };
  }, [
    year,
    month,
    selectedManufacturerId,
    selectedStatuses,
    pastDueOnly,
    dueWithin7Days,
    viewMode,
    visibleDateRange,
    showPONumbers,
  ]);

  // Fetch dashboard data - skip if request is null
  const {
    data: dashboardData,
    isLoading,
    refetch,
  } = useGetMonthlyDashboardQuery(dashboardRequest!, {
    skip: !dashboardRequest,
  });
  const [updateDueDate] = useUpdateOrderDueDateMutation();

  // Handle order click
  const handleOrderClick = useCallback((order: DashboardOrderSummary) => {
    setSelectedOrder(order);
    setPopupOpen(true);
  }, []);

  // Handle popup close
  const handlePopupClose = useCallback(() => {
    setPopupOpen(false);
    setSelectedOrder(null);
  }, []);

  // Handle drag and drop
  const handleEventDrop = useCallback(
    async (dropInfo: EventDropArg) => {
      if (!canReschedule) {
        dropInfo.revert();
        return;
      }

      const orderId = parseInt(dropInfo.event.id, 10);
      if (isNaN(orderId)) {
        console.error('Invalid order ID:', dropInfo.event.id);
        showToast('Invalid order ID. Please try again.', 'error');
        dropInfo.revert();
        return;
      }

      if (!dropInfo.event.start) {
        console.error('Event start date is missing');
        showToast('Unable to determine new due date. Please try again.', 'error');
        dropInfo.revert();
        return;
      }

      const newDueDate = DateTime.fromJSDate(dropInfo.event.start).toISODate();
      if (!newDueDate) {
        console.error('Failed to format due date');
        showToast('Invalid date format. Please try again.', 'error');
        dropInfo.revert();
        return;
      }

      try {
        await updateDueDate({
          orderId,
          request: { newDueDate },
        }).unwrap();
        showToast('Order due date updated successfully.', 'success');
        refetch();
      } catch (error) {
        console.error('Failed to update due date:', error);
        showToast('Failed to update order due date. Please try again.', 'error');
        // Revert the event to its original position on failure
        dropInfo.revert();
      }
    },
    [canReschedule, updateDueDate, refetch]
  );

  // Handle date navigation
  const handleDateChange = useCallback((date: Date) => {
    setCurrentDate(date);
  }, []);

  // Handle date range change (for week view)
  const handleDateRangeChange = useCallback((start: Date, end: Date) => {
    setVisibleDateRange({ start, end });
  }, []);

  // Handle view mode change
  const handleViewModeChange = useCallback(
    (_event: React.MouseEvent<HTMLElement>, newMode: 'month' | 'week' | null) => {
      if (newMode !== null) {
        setViewMode(newMode);
      }
    },
    []
  );

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Typography variant="h4" component="h1">
          Dashboard
        </Typography>
        {canManageOrders && (
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={() => setShowNewOrderWizard(true)}
            sx={{
              py: 0.5,
              height: 28,
              minHeight: 'unset',
              fontSize: '0.75rem',
            }}
          >
            Create Order
          </Button>
        )}
      </Box>

      {/* Statistics Section */}
      {isLoading ? (
        <Box
          sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}
        >
          <CircularProgress />
        </Box>
      ) : dashboardData ? (
        <DashboardStatistics
          statistics={dashboardData.statistics}
          pastDueCount={dashboardData.pastDueCount}
          manufacturerId={selectedManufacturerId}
          viewMode={viewMode}
          year={year}
          month={month}
          kpiMetrics={dashboardData.kpiMetrics}
          onRefresh={refetch}
        />
      ) : (
        <Box sx={{ p: 2 }}>
          <Typography color="error">Error loading dashboard data. Please try again.</Typography>
        </Box>
      )}

      {/* Filters Section */}
      <DashboardFilters
        isAdmin={isAdmin}
        selectedManufacturerId={selectedManufacturerId}
        onManufacturerChange={setSelectedManufacturerId}
        pastDueOnly={pastDueOnly}
        onPastDueChange={setPastDueOnly}
        dueWithin7Days={dueWithin7Days}
        onDueWithin7DaysChange={setDueWithin7Days}
        showPONumbers={showPONumbers}
        onShowPONumbersChange={setShowPONumbers}
        selectedStatuses={selectedStatuses}
        onStatusesChange={setSelectedStatuses}
        viewMode={viewMode}
        onViewModeChange={handleViewModeChange}
      />

      {/* Calendar Section */}
      <Paper elevation={2} sx={{ mt: 3, p: 2, position: 'relative', zIndex: 1 }}>
        <DashboardCalendar
          orders={dashboardData?.orders || []}
          currentDate={currentDate}
          viewMode={viewMode}
          showPONumbers={showPONumbers}
          canReschedule={canReschedule}
          onOrderClick={handleOrderClick}
          onEventDrop={handleEventDrop}
          onDateChange={handleDateChange}
          onDateRangeChange={handleDateRangeChange}
          isLoading={isLoading}
        />
      </Paper>

      {/* Order Detail Popup */}
      {selectedOrder && (
        <OrderDetailPopup
          open={popupOpen}
          order={selectedOrder}
          onClose={handlePopupClose}
          canReschedule={canReschedule}
          onRefresh={refetch}
        />
      )}

      {showNewOrderWizard && <NewOrderWizard onClose={() => setShowNewOrderWizard(false)} />}
    </Box>
  );
};

export default DashboardPage;
